// Libraries import
import { useEffect, useRef, useReducer, useState, useCallback } from "react";
import { connect } from "react-redux";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
// ...existing code...
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Tooltip from "@mui/material/Tooltip";
import { useNavigate, useParams, useLocation } from "react-router-dom";


// Styles and Assets
import styles from "./TakeAwaySettings.module.css";
import { ReactComponent as InfoIcon } from "../../../assets/images/rest_info.svg";
import { ReactComponent as TimeIcon } from "../../../assets/images/time.svg";

// Api, constants, hooks
import * as Api from "../../../api";
import { useGetRestaurantData, useUpdateRestaurantData } from "../../../api/useQueryHooks";

// Components
import Container from "../../../components/Container";
import AlertMsg from "../../../components/AlertMsg";
import Loader from "../../../components/Loader";
import RestaurantHeader from "../../../components/RestaurantHeader";
import SwitchInput from "../../../components/SwitchInput";
import SettingTextField from "../../../components/SettingTextField";

import AddSubMenuBtn from "../../../components/AddSubMenuBtn";
import DeliveryTakeAwayWindows from "../../../components/DeliveryTakeAwayWindows";

// Local Components and utilities
import EmailTemplate from "./EmailTemplate";
import { templateKey, reducer, initialState, defaultEmailTemplateObject } from "./TakeAwayReducer";
import { getTemplateKey } from "./constants";
import { updateRestaurantData } from "../../../redux/actions/userDataActions";
import { width } from "@mui/system";



const TakeAwaySettings = ({ restaurantData }) => {
  const navigate = useNavigate();
  const { rest_id } = useParams();
  const location = useLocation();

  const minCollectionRef = useRef(null);
  const [highlightMinCollection, setHighlightMinCollection] = useState(false);

  useEffect(() => {
    if (location.state?.highlight === 'minimumCollectionOrderAmount') {
      setHighlightMinCollection(true);
      setTimeout(() => {
        minCollectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
      const timer = setTimeout(() => setHighlightMinCollection(false), 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  const [state, dispatch] = useReducer(reducer, initialState);
  const [isUploading, setIsUploading] = useState(null);
  const [showPickupTemplate, setShowPickupTemplate] = useState(false);
  const [showDeliveryTemplate, setShowDeliveryTemplate] = useState(false);
  const [errors, setErrors] = useState({});
  const [changeObject, setChangeObject] = useState(new Set());
  const originalStateRef = useRef(null);

  // New state for delivery and takeaway windows
  const [deliveryWindows, setDeliveryWindows] = useState({
    sunday: null,
    monday: null,
    tuesday: null,
    wednesday: null,
    thursday: null,
    friday: null,
    saturday: null,
  });

  const [takeAwayWindows, setTakeAwayWindows] = useState({
    sunday: null,
    monday: null,
    tuesday: null,
    wednesday: null,
    thursday: null,
    friday: null,
    saturday: null,
  });

  // Wrapper functions to track changes
  const handleDeliveryWindowChange = (newDeliveryWindows) => {
    setDeliveryWindows(newDeliveryWindows);
    
    // Check if data has changed from original
    const originalData = originalStateRef.current?.deliveryWindow || {};
    const hasChanged = JSON.stringify(newDeliveryWindows) !== JSON.stringify(originalData);
    
    setChangeObject(prev => {
      const newChangeObject = new Set(prev);
      if (hasChanged) {
        newChangeObject.add("deliveryWindow");
      } else {
        newChangeObject.delete("deliveryWindow");
      }
      return newChangeObject;
    });
  };

  const handleTakeAwayWindowChange = (newTakeAwayWindows) => {
    setTakeAwayWindows(newTakeAwayWindows);
    
    // Check if data has changed from original
    const originalData = originalStateRef.current?.takeAwayWindow || {};
    const hasChanged = JSON.stringify(newTakeAwayWindows) !== JSON.stringify(originalData);
    
    setChangeObject(prev => {
      const newChangeObject = new Set(prev);
      if (hasChanged) {
        newChangeObject.add("takeAwayWindow");
      } else {
        newChangeObject.delete("takeAwayWindow");
      }
      return newChangeObject;
    });
  };

  // New fields for order email settings
  const [sendOrderEmail, setSendOrderEmail] = useState(false);
  const [restaurantEmails, setRestaurantEmails] = useState([]);

  // Email input state and handlers (moved after restaurantEmails is defined)
  const [emailInput, setEmailInput] = useState("");
  const [emailInputError, setEmailInputError] = useState("");

  const validateEmail = useCallback((email) => {
    if (!email) return "";
    // Simple email regex (without unnecessary escapes)
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([^<>()[\]\\.,;:\s@"]+\.)+[^<>()[\]\\.,;:\s@"]{2,})$/i;
    if (!re.test(email)) return "Invalid email address";
    if (restaurantEmails.includes(email)) return "Email already added";
    return "";
  }, [restaurantEmails]);

  const handleEmailInputChange = useCallback((val) => {
    setEmailInput(val);
    setEmailInputError(validateEmail(val));
  }, [validateEmail]);

  const handleAddEmail = useCallback(() => {
    const error = validateEmail(emailInput);
    setEmailInputError(error);
    if (!error && emailInput) {
      setRestaurantEmails([...restaurantEmails, emailInput]);
      setEmailInput("");
      // Mark as changed
      setChangeObject(prev => new Set([...prev, "restaurantEmails"]));
    }
  }, [emailInput, restaurantEmails, validateEmail]);

  const handleRemoveEmail = useCallback((idx) => {
    setRestaurantEmails(restaurantEmails.filter((_, i) => i !== idx));
    // Mark as changed
    setChangeObject(prev => new Set([...prev, "restaurantEmails"]));
  }, [restaurantEmails]);

  const [msgAlert, setMsgAlert] = useState({
    open: false,
    message: "",
    msgType: "error",
  });

  const { data: rest_data, isLoading } = useGetRestaurantData(rest_id);

  const updateStateFromRestData = useCallback((rest_data) => {
    const newState = {
      onlineOrder: rest_data.onlineOrder ? rest_data.onlineOrder : false,
      busyMode: rest_data.busyMode ? rest_data.busyMode : false,
      busyModeTime: rest_data.busyModeTime ? rest_data.busyModeTime : 0,
      defaultBusyModeTime: rest_data.defaultBusyModeTime ? rest_data.defaultBusyModeTime : "",
      minimumCollectionOrderAmount: rest_data.minimumCollectionOrderAmount ?? "",
      minimumDeliveryOrderAmount: rest_data.minimumDeliveryOrderAmount ?? "",
      deliveryRadius: rest_data.deliveryRadius ? rest_data.deliveryRadius : "",

      isDeliveryEnabled: rest_data.isDeliveryEnabled ? rest_data.isDeliveryEnabled : false,
      isTakeAwayEnabled: rest_data.isTakeAwayEnabled ? rest_data.isTakeAwayEnabled : false,
      deliveryETA: rest_data.deliveryETA ?? "",
      diningETA: rest_data.diningETA ?? "",
      [templateKey.pickUp]: {},
      [templateKey.delivery]: {},
    }

    const pickupTemplate = rest_data.emailTemplate?.[0]?.onlineOrderConfirmation[0] ?? {};
    const deliveryTemplate = rest_data.emailTemplate?.[0]?.onlineOrderConfirmation[1] ?? {};

    const keys = Object.keys(defaultEmailTemplateObject());

    for (const key of keys) {
      newState[templateKey.pickUp][key] = pickupTemplate[key] ?? "";
      newState[templateKey.delivery][key] = deliveryTemplate[key] ?? "";
    }

  // Load sendOrderEmail and restaurantEmails from rest_data if available
  setSendOrderEmail(rest_data.sendOrderEmail ?? false);
  setRestaurantEmails(rest_data.restaurantEmails && rest_data.restaurantEmails.length > 0 ? rest_data.restaurantEmails : []);

  originalStateRef.current = newState;
  dispatch({ type: "STATE", payload: newState });
  }, []);

  // Load restaurant data 
  useEffect(() => {
    if (rest_data) {
      updateRestaurantData(rest_data);
      updateStateFromRestData(rest_data);
      
      // Set window data
      const daysOfWeek = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
      const deliveryWindowData = {};
      const takeAwayWindowData = {};
      
      daysOfWeek.forEach(day => {
        deliveryWindowData[day] = rest_data.deliveryWindow?.[day] || null;
        takeAwayWindowData[day] = rest_data.takeAwayWindow?.[day] || null;
      });
      
      setDeliveryWindows(deliveryWindowData);
      setTakeAwayWindows(takeAwayWindowData);
      
      // Store original window data for change tracking
      if (originalStateRef.current) {
        originalStateRef.current.deliveryWindow = deliveryWindowData;
        originalStateRef.current.takeAwayWindow = takeAwayWindowData;
      }
    }
  }, [rest_data, updateStateFromRestData]);

  const updateRestaurant = useUpdateRestaurantData(response => {
    originalStateRef.current = state;
    setMsgAlert({ open: true, message: response.msg ?? response.message ?? "Restaurant data updated successfully", msgType: "success" });
    resetState();
  },
    (response) => {
      setMsgAlert({ open: true, message: response.msg ?? response.message ?? "Error while updating data", msgType: "error" });
    });

  const getServerDataFromState = (rest_data, state) => {
    const getTemplateData = () => {
      const keys = Object.keys(defaultEmailTemplateObject());
      const returnArr = [];
      const pickupTemplate = { ...rest_data.emailTemplate?.[0]?.onlineOrderConfirmation[0] ?? {} };
      const deliveryTemplate = { ...rest_data.emailTemplate?.[0]?.onlineOrderConfirmation[1] ?? {} };

      for (const key of keys) {
        if (key === "headerImage" || key === "footerImage") {
          pickupTemplate[key] = !(restaurantData.emailTemplate?.[0]?.onlineOrderConfirmation[0][key])
            && !state[templateKey.pickUp][key] ? null : typeof state[templateKey.pickUp][key] === "string" || state[templateKey.pickUp][key] === null ?
            state[templateKey.pickUp][key] : state[templateKey.pickUp][key]?.location;
          deliveryTemplate[key] = !(restaurantData.emailTemplate?.[0]?.onlineOrderConfirmation[1][key])
            && !state[templateKey.delivery][key] ? null : typeof state[templateKey.delivery][key] === "string" || state[templateKey.delivery][key] === null ?
            state[templateKey.delivery][key] : state[templateKey.delivery][key]?.location;
        } else {
          pickupTemplate[key] = !(restaurantData.emailTemplate?.[0]?.onlineOrderConfirmation[0][key])
            && !state[templateKey.pickUp][key] ? null : state[templateKey.pickUp][key];
          deliveryTemplate[key] = !(restaurantData.emailTemplate?.[0]?.onlineOrderConfirmation[1][key])
            && !state[templateKey.delivery][key] ? null : state[templateKey.delivery][key];
        }
      }
      returnArr.push(pickupTemplate);
      returnArr.push(deliveryTemplate);
      return returnArr;
    }

    return {
      ...rest_data,
      sendOrderEmail,
      restaurantEmails,
      onlineOrder: state.onlineOrder,
      busyMode: state.busyMode,
      busyModeTime: state.busyModeTime,
      defaultBusyModeTime: state.defaultBusyModeTime,
      minimumCollectionOrderAmount: state.minimumCollectionOrderAmount,
      minimumDeliveryOrderAmount: state.minimumDeliveryOrderAmount,
      deliveryRadius: state.deliveryRadius,
      deliveryETA: state.deliveryETA,
      diningETA: state.diningETA,
      deliveryWindow: deliveryWindows,
      takeAwayWindow: takeAwayWindows,
      isDeliveryEnabled: state.isDeliveryEnabled,
      isTakeAwayEnabled: state.isTakeAwayEnabled,
      // Just preserving the id of the email template in the server data
      emailTemplate: [{ ...restaurantData.emailTemplate?.[0], onlineOrderConfirmation: getTemplateData() }]
    };
  }

  const uploadFile = (field, file, fieldKey) => {
    setIsUploading({ fieldKey, field });
    const formData = new FormData();
    formData.append("image", file);
    Api.uploadImageFile(formData).then((response) => {
      if (response.success) {
        handleChange(field, response.data, fieldKey);
      } else {
        setMsgAlert({
          open: true,
          message: response.msg ?? "Error while uploading image",
          msgType: "error",
        });
      }
    }).finally(() => setIsUploading(null));
  };


  const onSaveGeneralSettings = () => {
    if (Object.keys(errors).length > 0) return;
    const serverData = getServerDataFromState(restaurantData, state);
    updateRestaurant.mutate(serverData);
  };





  const onCancel = () => {
    dispatch({ type: "STATE", payload: originalStateRef.current });
    
    // Reset window data to original values
    if (originalStateRef.current?.deliveryWindow) {
      setDeliveryWindows(originalStateRef.current.deliveryWindow);
    }
    if (originalStateRef.current?.takeAwayWindow) {
      setTakeAwayWindows(originalStateRef.current.takeAwayWindow);
    }
    
    resetState();
  };

  const resetState = () => {
    setChangeObject(new Set());
    setErrors({});
  }

  const handleChange = (field, value, fieldKey = null) => {
    // Detect Change first (before validation changes errors)
    const newChangeObject = new Set(changeObject);
    const originalValue = fieldKey ? originalStateRef.current[fieldKey][field] : originalStateRef.current[field];
    
    // Compare values properly - handle null/undefined/empty string cases
    const normalizedValue = value === null || value === undefined ? "" : value;
    const normalizedOriginalValue = originalValue === null || originalValue === undefined ? "" : originalValue;
    
    if (normalizedOriginalValue === normalizedValue || 
        (normalizedOriginalValue === "" && normalizedValue === "") ||
        (normalizedOriginalValue instanceof Date && normalizedValue instanceof Date && 
         normalizedOriginalValue.getTime() === normalizedValue.getTime())) {
      newChangeObject.delete(fieldKey ? getTemplateKey(fieldKey, field) : field);
    } else {
      newChangeObject.add(fieldKey ? getTemplateKey(fieldKey, field) : field);
    }
    setChangeObject(newChangeObject);

    // Detect Error
    switch (field) {
      // Only these fields are required. For the template fields, we are checking in the EmailTemplate component
      case "deliveryETA":
      case "diningETA":
        const etaErrors = { ...errors };
        if (!value || value === "") {
          etaErrors[field] = `${field === "deliveryETA" ? "Delivery" : "Pickup"} ETA is required`;
        } else {
          delete etaErrors[field];
        }
        setErrors(etaErrors);
        break;
      default: ;
    }

    dispatch({ type: fieldKey ? fieldKey : field, payload: fieldKey ? { subField: field, value } : value });
  };

  const onSetBusyModeTime = (type) => {
    let newVal = state.busyModeTime;
    if (type === "+") newVal += 5;
    else {
      newVal -= 5;
      if (newVal < 0) newVal = 0;
    }
    // Only looking for changes and not Error as it's not a required field
    if (newVal !== originalStateRef.current.busyModeTime) {
      if (state.busyMode) setChangeObject(new Set([...changeObject, "busyModeTime"]));
    } else {
      const newChangeSet = new Set(changeObject);
      newChangeSet.delete("busyModeTime");
      setChangeObject(newChangeSet);
    }

    dispatch({ type: "busyModeTime", payload: newVal });
  };

  const ToggleSwitchLabel = ({ label, info }) => {
    return (
      <Typography
        className={styles?.["toggle-label"]}
        sx={{ ...styles1.labelStyle, path: { fill: "#000" } }}
      >
        {label}
        {info ? (
          <Tooltip
            componentsProps={{
              tooltip: {
                sx: {
                  marginTop: "0px !important",
                  marginBottom: "0px !important",
                  backgroundColor: "#EEE",
                  color: "#000",
                },
              },
            }}
            title={info}
            placement="top"
          >
            <InfoIcon
              height={15}
              width={15}
              style={{ cursor: "pointer", marginLeft: 4 }}
            />
          </Tooltip>
        ) : null}
      </Typography>
    );
  };

  const saveBtnDisabled = changeObject.size === 0 || Object.keys(errors).length > 0;

  return (
    <Container
      page={"restaurants"}
      searchVal={""}
      onSearch={() => null}
      searchLabel=""
    >
      {isLoading ? (
        <Loader height={{ height: window.innerHeight - 150 }} />
      ) : (
        <Grid container item xs={12}>
          <Grid item xs={12} mb={2}>
            <RestaurantHeader
              title={"Take Away Settings"}
              onBackBtnClick={() => navigate("/restaurant/" + rest_id)}
            />
          </Grid>
          <Grid
            item
            xs={12}
            className={styles?.["main-view"]}
            px={3}
            py={2}
          >
            <Grid
              item
              xs={12}
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              mt={1}
              mb={2}
            >
              <Box className={styles?.["switch-input-view"]}>
                <ToggleSwitchLabel
                  label={"Online Order"}
                  info={"Toggle option for user to place order online"}
                />
                <SwitchInput
                  checked={state.onlineOrder}
                  onChange={(val) => handleChange("onlineOrder", val)}
                />
              </Box>
              <Box className={styles?.["switch-input-view"]}>
                <ToggleSwitchLabel label={"Delivery"} info={""} />
                <SwitchInput
                  checked={state.isDeliveryEnabled}
                  onChange={(val) => handleChange("isDeliveryEnabled", val)}
                />
              </Box>
              <Box className={styles?.["switch-input-view"]}>
                <ToggleSwitchLabel label={"TakeAway"} info={""} />
                <SwitchInput
                  checked={state.isTakeAwayEnabled}
                  onChange={(val) => handleChange("isTakeAwayEnabled", val)}
                />
              </Box>
              <Box className={styles?.["switch-input-view"]}>
                <ToggleSwitchLabel label={"Busy Mode"} info={""} />
                <SwitchInput
                  checked={state.busyMode}
                  onChange={(val) => handleChange("busyMode", val)}
                />
              </Box>
              {state.busyMode ? (
                <Box className={styles?.["switch-input-view"]}>
                  <ToggleSwitchLabel
                    label={"Busy Mode Time (in mins)"}
                    info={""}
                  />
                  <AddSubMenuBtn
                    count={state.busyModeTime}
                    onAddSub={onSetBusyModeTime}
                  />
                </Box>
              ) : null}
            </Grid>
            <Grid container item xs={12} spacing={2}>
              <Grid item xs={12} md={3} mb={2}>
                <SettingTextField
                  value={state.defaultBusyModeTime}
                  setValue={(val) => handleChange("defaultBusyModeTime", val)}
                  placeholder={""}
                  required={true}
                  label={"Default Busy Mode Time"}
                  labelStyle={styles1.labelStyle}
                  variant={"filled"}
                  border={"1px solid #AAA"}
                  error={errors?.defaultBusyModeTime}
                />
              </Grid>
              <Grid item xs={12} md={3} mb={2}>
                <SettingTextField
                  value={state.deliveryRadius}
                  setValue={(val) => handleChange("deliveryRadius", val)}
                  placeholder={""}
                  required={true}
                  label={"Delivery Radius"}
                  labelStyle={styles1.labelStyle}
                  variant={"filled"}
                  border={"1px solid #AAA"}
                  error={errors?.deliveryRadius}
                />
              </Grid>
              <Grid item xs={12} md={3} mb={2}>
                <Box
                  ref={minCollectionRef}
                  sx={{
                    borderRadius: '8px',
                    transition: 'box-shadow 0.4s ease, background-color 0.4s ease',
                    ...(highlightMinCollection && {
                      backgroundColor: '#fffde7',
                      boxShadow: '0 0 0 3px #f9a825',
                    }),
                  }}
                >
                  <SettingTextField
                    value={state.minimumCollectionOrderAmount}
                    setValue={(val) =>
                      handleChange("minimumCollectionOrderAmount", val)
                    }
                    placeholder={""}
                    label={"Minimum Collection Order Amount"}
                    labelStyle={styles1.labelStyle}
                    required={true}
                    variant={"filled"}
                    border={"1px solid #AAA"}
                    error={errors?.minimumCollectionOrderAmount}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={3} mb={2}>
                <SettingTextField
                  value={state.minimumDeliveryOrderAmount}
                  setValue={(val) =>
                    handleChange("minimumDeliveryOrderAmount", val)
                  }
                  label={"Minimum Delivery Order Amount"}
                  labelStyle={styles1.labelStyle}
                  placeholder={""}
                  required={true}
                  variant={"filled"}
                  border={"1px solid #AAA"}
                  error={errors?.minimumDeliveryOrderAmount}
                />
              </Grid>
            </Grid>

            <Grid container item xs={12} spacing={2}>
              <Grid item xs={12} md={3} mb={2}>
                <SettingTextField
                  value={state.deliveryETA}
                  setValue={(val) => handleChange("deliveryETA", val)}
                  placeholder={""}
                  required={true}
                  label={"Delivery ETA"}
                  labelStyle={styles1.labelStyle}
                  variant={"filled"}
                  border={"1px solid #AAA"}
                  leftView={<TimeIcon width={20} height={20} />}
                  rightView={
                    <Typography sx={styles1.minutesText}>
                      minutes
                    </Typography>
                  }
                  error={errors?.deliveryETA}
                />
              </Grid>
              <Grid item xs={12} md={3} mb={2}>
                <SettingTextField
                  value={state.diningETA}
                  setValue={(val) => handleChange("diningETA", val)}
                  placeholder={""}
                  required={true}
                  label={"Pickup ETA"}
                  labelStyle={styles1.labelStyle}
                  variant={"filled"}
                  border={"1px solid #AAA"}
                  leftView={<TimeIcon width={20} height={20} />}
                  rightView={
                    <Typography sx={styles1.minutesText}>
                      minutes
                    </Typography>
                  }
                  error={errors?.diningETA}
                />
              </Grid>
            </Grid>

            {/* Send Order Email & Restaurant Emails - Compact Attached Sections */}
            <Grid item xs={12} mb={2}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, fontFamily: "GilroySemiBold", fontSize: '1rem' }}>Email Notification Settings</Typography>
              <Box sx={{ border: '1px solid #DDD', borderRadius: '8px', overflow: 'hidden' }}>
                {/* Toggle Section */}
                <Box sx={{ p: 1.5, borderBottom: '1px solid #DDD', backgroundColor: '#FAFAFA' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontWeight: 600, fontFamily: "GilroySemiBold", fontSize: '0.9rem' }}>Send Order Email</Typography>
                    <SwitchInput
                      checked={sendOrderEmail}
                      onChange={(val) => {
                        setSendOrderEmail(val);
                        setChangeObject(prev => new Set([...prev, "sendOrderEmail"]));
                      }}
                    />
                  </Box>
                </Box>

                {/* Email Input Section - Shows when enabled */}
                {sendOrderEmail && (
                  <Box sx={{ p: 1.5, borderBottom: '1px solid #DDD', backgroundColor: '#FFF' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ mb: 0.5, fontWeight: 600, fontFamily: "GilroySemiBold", fontSize: '0.85rem' }}>
                          Add Email
                        </Typography>
                        <SettingTextField
                          value={emailInput || ""}
                          setValue={handleEmailInputChange}
                          placeholder="Enter email"
                          type="email"
                          variant="filled"
                          error={emailInputError}
                          sx={{ flex: 1 }}
                        />
                      </Box>
                      <Button
                        onClick={handleAddEmail}
                        variant="contained"
                        color="primary"
                        size="small"
                        sx={{ minWidth: 80 }}
                        disabled={!emailInput || emailInputError}
                        startIcon={<AddIcon />}
                      >
                        Add
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Added Emails List Section */}
                {sendOrderEmail && restaurantEmails.length > 0 && (
                  <Box sx={{ p: 1.5, backgroundColor: '#F0F7FF' }}>
                    <Typography sx={{ mb: 1, fontWeight: 600, fontFamily: "GilroySemiBold", fontSize: '0.85rem', color: '#1976D2' }}>
                      Added ({restaurantEmails.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {restaurantEmails.map((email, idx) => (
                        <Box 
                          key={idx} 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            p: 1,
                            backgroundColor: '#FFF',
                            border: '1px solid #E0E0E0',
                            borderRadius: '4px',
                            '&:hover': {
                              backgroundColor: '#FAFAFA',
                              borderColor: '#BBB'
                            }
                          }}
                        >
                          <Typography sx={{ fontFamily: "GilroySemiBold", fontSize: '0.85rem', color: '#333' }}>
                            {email}
                          </Typography>
                          <Tooltip title="Remove">
                            <IconButton
                              onClick={() => handleRemoveEmail(idx)}
                              color="error"
                              size="small"
                              sx={{ '&:hover': { backgroundColor: '#FFEBEE' } }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} mb={4}>
              <DeliveryTakeAwayWindows
                deliveryWindow={deliveryWindows}
                takeAwayWindow={takeAwayWindows}
                onDeliveryWindowChange={handleDeliveryWindowChange}
                onTakeAwayWindowChange={handleTakeAwayWindowChange}
                errors={errors}
              />
            </Grid>

            
            
            <Grid container item xs={12} spacing={2}>
              < EmailTemplate
                title={"Pickup Email Template Settings"}
                showSection={showPickupTemplate}
                toggleSection={setShowPickupTemplate}
                templateData={state[templateKey.pickUp]}
                handleChange={handleChange}
                errors={errors}
                fieldKey={templateKey.pickUp}
                uploadFile={uploadFile}
                isUploading={isUploading}
                setErrors={setErrors}
              />
              < EmailTemplate
                title={"Delivery Email Template Settings"}
                showSection={showDeliveryTemplate}
                toggleSection={setShowDeliveryTemplate}
                templateData={state[templateKey.delivery]}
                handleChange={handleChange}
                errors={errors}
                fieldKey={templateKey.delivery}
                uploadFile={uploadFile}
                isUploading={isUploading}
                setErrors={setErrors}
              />
            </Grid>

            <Grid container item xs={12} justifyContent={"flex-end"} mt={2}>
              <Grid item xs={12} md={5}>
                <Box className={styles?.["btn-main-View"]} mt={2}>
                  <Button
                    type="button"
                    className={styles?.["cancel-btn"]}
                    onClick={onCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    disabled={saveBtnDisabled}
                    sx={{
                      opacity: saveBtnDisabled ? 0.2 : 1,
                    }}
                    className={styles?.["save-btn"]}
                    onClick={onSaveGeneralSettings}
                  >
                    Save
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
      <AlertMsg
        msgAlert={msgAlert}
        onCloseAlertMsg={() =>
          setMsgAlert({ ...msgAlert, open: false })
        }
      />
    </Container>
  );
};

const mapStateToProps = (state) => ({
  restaurantData: state.userData.restaurantData,
});

const mapDispatchToProps = (dispatch) => ({
  updateRestaurantData: (data) => dispatch(updateRestaurantData(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(TakeAwaySettings);

const styles1 = {
  labelStyle: {
    fontSize: 14,
    fontFamily: "GilroySemiBold",
  },
  minutesText: {
    fontSize: "0.875rem",
    fontFamily: "GilroySemiBold !important",
    color: "#666",
  },
  textFieldSlotProps: {
    sx: {
      "&.MuiTextField-root": {
        width: "100%",
      },
      "& .MuiInputBase-root": {
        border: "1px solid #AAA",
        borderRadius: "12px",
        height: 46,
        px: 1,
        mt: 3,
        "&:before, :after, :hover:not(.Mui-disabled):before": {
          border: 0,
        },
        "&.Mui-error:after": {
          border: 0,
        },
      },
      svg: { color: "#000", fontSize: 18 },
      "& .MuiButtonBase-root": {
        width: 35,
        height: 35,
      },
    },
  },
};
